/**
 * @fileoverview Pendo tracking hook for Pending Actions feature.
 * Uses the standardized tracking hook for consistent event structure.
 */

import { useCallback } from "react";
import { useStandardizedTracking, EventNames, ContentTypes, LayoutTypes } from "./useStandardizedTracking";

const FEATURE_GROUP = "pending_actions";

/**
 * Hook for tracking Pending Actions feature events
 * @returns {Object} Tracking functions for pending actions
 */
export const usePendingActionsPendoTracking = () => {
  const { track } = useStandardizedTracking();

  /**
   * Track Pending Actions section impression (page load)
   * @param {Object} data - Section data
   * @param {number} data.eligiblePatientsCount - Number of eligible M3P patients
   * @param {number} data.expressEnrollInvitesEligibleCount - Number of eligible Express Enroll Invites patients
   * @param {number} data.expressEnrollInvitesMinThreshold - Minimum threshold for Express Enroll Invites card display
   * @param {number} data.overdueTasksCount - Number of overdue tasks
   * @param {number} data.pendingTodayCount - Number of tasks pending today
   */
  const trackSectionImpression = useCallback(
    ({
      eligiblePatientsCount,
      minThreshold = 5,
      expressEnrollInvitesEligibleCount = 0,
      expressEnrollInvitesMinThreshold = 5,
      overdueTasksCount,
      pendingTodayCount
    }) => {
      track(EventNames.BOX_IMPRESSION, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "pending_actions_section",
        contentType: ContentTypes.PATIENT,
        layout: LayoutTypes.CARD,
        additionalInfo: {
          eligible_patients_count: eligiblePatientsCount,
          express_enroll_invites_eligible_count: expressEnrollInvitesEligibleCount,
          overdue_tasks_count: overdueTasksCount,
          pending_today_count: pendingTodayCount,
          cards_displayed: [
            eligiblePatientsCount >= minThreshold ? "m3p_eligible" : null,
            expressEnrollInvitesEligibleCount >= expressEnrollInvitesMinThreshold
              ? "express_enroll_invites_eligible"
              : null,
            overdueTasksCount > 0 || pendingTodayCount > 0 ? "tasks" : null
          ].filter(Boolean)
        }
      });
    },
    [track]
  );

  /**
   * Track M3P card impression
   * @param {number} eligibleCount - Number of eligible patients displayed
   * @param {number} totalEligible - Total eligible patients
   */
  const trackM3PCardImpression = useCallback(
    (eligibleCount, totalEligible) => {
      track(EventNames.BOX_IMPRESSION, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "m3p_eligible_card",
        contentType: ContentTypes.PATIENT,
        layout: LayoutTypes.CARD,
        additionalInfo: {
          eligible_count: eligibleCount,
          total_eligible: totalEligible
        }
      });
    },
    [track]
  );

  /**
   * Track Send Invites button click
   * @param {number} patientsCount - Number of patients being sent to
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackSendInvitesClick = useCallback(
    (patientsCount, actionType) => {
      track(EventNames.BUTTON_CLICK, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "send_invites_button",
        contentType: ContentTypes.BUTTON,
        additionalInfo: {
          patients_count: patientsCount,
          action: "bulk_send",
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track Send Invites success
   * @param {number} sentCount - Number of patients successfully sent to
   * @param {number} failedCount - Number of failed sends
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackSendInvitesSuccess = useCallback(
    (sentCount, failedCount, actionType) => {
      track(EventNames.SEND_LINK_SUCCESS, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "send_invites_success",
        contentType: ContentTypes.PATIENT,
        additionalInfo: {
          sent_count: sentCount,
          failed_count: failedCount,
          total_attempted: sentCount + failedCount,
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track Review Patients button click
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackReviewPatientsClick = useCallback(
    (actionType) => {
      track(EventNames.BUTTON_CLICK, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "review_patients_button",
        contentType: ContentTypes.BUTTON,
        additionalInfo: {
          action: "open_review_modal",
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track Tasks card impression
   * @param {number} overdueCount - Number of overdue tasks
   * @param {number} pendingTodayCount - Number of tasks pending today
   */
  const trackTasksCardImpression = useCallback(
    (overdueCount, pendingTodayCount) => {
      track(EventNames.BOX_IMPRESSION, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "tasks_card",
        contentType: "Task",
        layout: LayoutTypes.CARD,
        additionalInfo: {
          overdue_count: overdueCount,
          pending_today_count: pendingTodayCount
        }
      });
    },
    [track]
  );

  /**
   * Track View Tasks click (navigate to tasks page)
   * @param {string} taskType - Type of tasks being viewed ('overdue' or 'pending_today')
   */
  const trackViewTasksClick = useCallback(
    (taskType) => {
      track(EventNames.BUTTON_CLICK, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "view_tasks_button",
        contentType: ContentTypes.BUTTON,
        additionalInfo: {
          task_type: taskType,
          action: "navigate_to_tasks"
        }
      });
    },
    [track]
  );

  /**
   * Track Auto-approval banner impression
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackAutoApprovalBannerImpression = useCallback(
    (actionType) => {
      track(EventNames.BANNER_IMPRESSION, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "auto_approval_prompt",
        contentType: "Banner",
        layout: "banner",
        additionalInfo: {
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track Auto-approval enable click
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackAutoApprovalEnableClick = useCallback(
    (actionType) => {
      track(EventNames.BUTTON_CLICK, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "auto_approval_enable_button",
        contentType: ContentTypes.BUTTON,
        additionalInfo: {
          action: "enable_auto_send",
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track Auto-approval dismiss click
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackAutoApprovalDismissClick = useCallback(
    (actionType) => {
      track(EventNames.BUTTON_CLICK, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "auto_approval_dismiss_button",
        contentType: ContentTypes.BUTTON,
        additionalInfo: {
          action: "dismiss_auto_send_prompt",
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track Auto-approval success (enabled successfully)
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackAutoApprovalEnabled = useCallback(
    (actionType) => {
      track(EventNames.FORM_SUBMITTED, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "auto_approval_enabled",
        contentType: ContentTypes.APPLICATION,
        additionalInfo: {
          action: "auto_send_enabled",
          action_type: actionType
        }
      });
    },
    [track]
  );

  /**
   * Track tooltip info icon click
   * @param {string} tooltipType - Type of tooltip ('m3p_info', 'tasks_info')
   * @param {string} actionType - Action type identifier (e.g. 'paytient', 'express_enroll_invites')
   */
  const trackTooltipClick = useCallback(
    (tooltipType, actionType) => {
      track(EventNames.BUTTON_CLICK, {
        featureGroup: FEATURE_GROUP,
        pageKey: "home",
        contentKey: "tooltip_info_icon",
        contentType: ContentTypes.BUTTON,
        additionalInfo: {
          tooltip_type: tooltipType,
          action_type: actionType
        }
      });
    },
    [track]
  );

  return {
    trackSectionImpression,
    trackM3PCardImpression,
    trackSendInvitesClick,
    trackSendInvitesSuccess,
    trackReviewPatientsClick,
    trackTasksCardImpression,
    trackViewTasksClick,
    trackAutoApprovalBannerImpression,
    trackAutoApprovalEnableClick,
    trackAutoApprovalDismissClick,
    trackAutoApprovalEnabled,
    trackTooltipClick
  };
};

export default usePendingActionsPendoTracking;
